import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Checkbox, Space, Dropdown, Menu, Input, Select, DatePicker, InputNumber, message, Alert } from 'antd';
import { SaveOutlined, DownOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { GetUnitsApi } from '../../api/GetUnitsApi';
import { GetUnitPositionsApi2 } from '../../api/units/GetUnitPositionsApi2';
import { GetPlanGroupsApi } from '../../api/GetPlanGroupsApi';
import GetScalesTableApi from '../../api/scales/GetScalesTableApi';
import { GetPlanAttributesApi } from '../../api/bulk-plan/GetPlanAttributesApi';

const { Option } = Select;

function PlanPreviewTable({ hierarchy, selectedPlans, onSelectionChange, onHierarchyUpdate, performanceCycleId }) {
  const [flattenedPlans, setFlattenedPlans] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [editingData, setEditingData] = useState({});
  
  // Dropdown data
  const [units, setUnits] = useState([]);
  const [positions, setPositions] = useState([]); // Changed from users to positions
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [planAttributes, setPlanAttributes] = useState([]);
  const [dropdownAttributeValues, setDropdownAttributeValues] = useState({});
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Load dropdown data on mount
  useEffect(() => {
    loadDropdownData();
  }, [performanceCycleId]);

  async function loadDropdownData() {
    if (loadingDropdowns) return; // Prevent multiple simultaneous loads
    
    console.log('Loading dropdown data...');
    setLoadingDropdowns(true);
    try {
      // Load units
      try {
        console.log('Loading units...');
        const unitsResponse = await GetUnitsApi();
        console.log('Units response:', unitsResponse);
        if (unitsResponse && unitsResponse.success && unitsResponse.data) {
          const unitsArray = Array.isArray(unitsResponse.data) ? unitsResponse.data : [];
          console.log('Setting units:', unitsArray.length, 'units');
          setUnits(unitsArray);
        } else {
          console.error('Invalid units response');
          setUnits([]);
        }
      } catch (error) {
        console.error('Failed to load units:', error);
        setUnits([]);
      }

      // Note: Positions will be loaded dynamically when a unit is selected during editing

      // Load plan groups (get all without pagination)
      try {
        const planGroupsResponse = await GetPlanGroupsApi(1, 1000);
        if (planGroupsResponse && planGroupsResponse.success && planGroupsResponse.data) {
          setPlanGroups(Array.isArray(planGroupsResponse.data) ? planGroupsResponse.data : []);
        }
      } catch (error) {
        console.error('Failed to load plan groups:', error);
      }

      // Load scales (get all without pagination)
      try {
        const scalesResponse = await GetScalesTableApi(1, 1000);
        if (scalesResponse && scalesResponse.success && scalesResponse.data) {
          setScales(Array.isArray(scalesResponse.data) ? scalesResponse.data : []);
        }
      } catch (error) {
        console.error('Failed to load scales:', error);
      }

      // Load plan attributes with dropdown values
      try {
        const attributesResponse = await GetPlanAttributesApi(performanceCycleId);
        if (attributesResponse && attributesResponse.success && attributesResponse.data) {
          const attrs = Array.isArray(attributesResponse.data) ? attributesResponse.data : [];
          setPlanAttributes(attrs);
          
          // Build dropdown values map
          const dropdownMap = {};
          attrs.forEach(attr => {
            if (attr && attr.valueType === 'dropdown' && Array.isArray(attr.dropdownValues)) {
              dropdownMap[attr.name] = attr.dropdownValues;
            }
          });
          setDropdownAttributeValues(dropdownMap);
        }
      } catch (error) {
        console.error('Failed to load plan attributes:', error);
      }
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    } finally {
      setLoadingDropdowns(false);
    }
  }

  // Flatten hierarchy for table display
  useEffect(() => {
    const flattened = flattenHierarchy(hierarchy);
    setFlattenedPlans(flattened);
  }, [hierarchy]);

  // Flatten hierarchy recursively
  function flattenHierarchy(plans, level = 0) {
    const result = [];
    plans.forEach(plan => {
      result.push({ ...plan, level });
      if (plan.children && plan.children.length > 0) {
        result.push(...flattenHierarchy(plan.children, level + 1));
      }
    });
    return result;
  }

  // Check if row is being edited
  const isEditing = (record) => record.id === editingKey;

  // Load positions for a specific unit
  async function loadPositionsForUnit(unitId) {
    if (!unitId) {
      setPositions([]);
      return;
    }
    
    try {
      const response = await GetUnitPositionsApi2(unitId);
      if (response && response.message === 'success' && response.data) {
        setPositions(Array.isArray(response.data) ? response.data : []);
      } else {
        setPositions([]);
      }
    } catch (error) {
      console.error('Failed to load positions for unit:', error);
      setPositions([]);
    }
  }

  // Start editing a row
  async function handleEdit(record) {
    // Don't allow editing if dropdowns are still loading
    if (loadingDropdowns) {
      message.warning('Please wait for dropdown data to load');
      return;
    }
    
    setEditingKey(record.id);
    // Deep clone to avoid mutating original data
    setEditingData({
      ...record,
      customAttributes: { ...(record.customAttributes || {}) },
    });
    
    // Load positions for the current unit
    const selectedUnit = units.find(u => u.name === record.unit);
    if (selectedUnit) {
      await loadPositionsForUnit(selectedUnit.id);
    }
  }

  // Cancel editing
  function handleCancelEdit() {
    setEditingKey('');
    setEditingData({});
  }

  // Save edited row
  function handleSave() {
    const updatedHierarchy = updatePlanInHierarchy(hierarchy, editingKey, editingData);
    onHierarchyUpdate(updatedHierarchy);
    setEditingKey('');
    setEditingData({});
    message.success('Plan updated successfully');
  }

  // Update plan in hierarchy recursively
  function updatePlanInHierarchy(plans, planId, newData) {
    return plans.map(plan => {
      if (plan.id === planId) {
        return { ...plan, ...newData };
      }
      if (plan.children && plan.children.length > 0) {
        return {
          ...plan,
          children: updatePlanInHierarchy(plan.children, planId, newData),
        };
      }
      return plan;
    });
  }

  // Handle field change during editing
  async function handleFieldChange(field, value) {
    if (field.startsWith('customAttributes.')) {
      const attrName = field.replace('customAttributes.', '');
      setEditingData({
        ...editingData,
        customAttributes: {
          ...editingData.customAttributes,
          [attrName]: value,
        },
      });
    } else {
      const updates = { [field]: value };
      
      // If unit changes, clear assignedTo and load new positions
      if (field === 'unit') {
        updates.assignedTo = null;
        const selectedUnit = units.find(u => u.name === value);
        if (selectedUnit) {
          await loadPositionsForUnit(selectedUnit.id);
        } else {
          setPositions([]);
        }
      }
      
      setEditingData({ ...editingData, ...updates });
    }
  }

  // Handle individual plan selection
  function handlePlanSelect(planId, checked) {
    const newSelection = checked
      ? [...selectedPlans, planId]
      : selectedPlans.filter(id => id !== planId);
    onSelectionChange(newSelection);
  }

  // Handle select all
  function handleSelectAll(checked) {
    setSelectAll(checked);
    if (checked) {
      const allPlanIds = flattenedPlans.map(p => p.id);
      onSelectionChange(allPlanIds);
    } else {
      onSelectionChange([]);
    }
  }

  // Handle batch selection
  function handleBatchSelect(count) {
    if (count === 'all') {
      handleSelectAll(true);
    } else {
      const selectedIds = flattenedPlans.slice(0, count).map(p => p.id);
      onSelectionChange(selectedIds);
    }
  }

  // Get plan type badge
  function getPlanTypeBadge(type) {
    const typeConfig = {
      TOP_PLAN: { color: 'blue', text: 'Top Plan' },
      CASCADE_PLAN: { color: 'green', text: 'Cascade Plan' },
      SUB_CASCADE_PLAN: { color: 'orange', text: 'Sub-Cascade' },
    };
    const config = typeConfig[type] || { color: 'default', text: type };
    return <Tag color={config.color}>{config.text}</Tag>;
  }

  // Get cell style based on validation status
  function getCellStyle(record, fieldName) {
    if (!record.validationStatus) return {};
    
    let isValid = true;
    
    switch (fieldName) {
      case 'unit':
        isValid = record.validationStatus.unitMatched;
        break;
      case 'assignedTo':
        isValid = record.validationStatus.assignedToMatched;
        break;
      case 'planGroup':
        isValid = record.validationStatus.planGroupMatched;
        break;
      case 'scaleId':
        isValid = record.validationStatus.scaleMatched;
        break;
      default:
        if (record.validationStatus.customAttributesMatched && record.validationStatus.customAttributesMatched[fieldName] !== undefined) {
          isValid = record.validationStatus.customAttributesMatched[fieldName];
        }
    }
    
    return {
      border: `2px solid ${isValid ? '#52c41a' : '#faad14'}`,
      borderRadius: '4px',
      padding: '4px',
    };
  }

  // Batch selection menu
  const batchMenu = (
    <Menu onClick={({ key }) => handleBatchSelect(key)}>
      <Menu.Item key="1">Select First 1</Menu.Item>
      <Menu.Item key="10">Select First 10</Menu.Item>
      <Menu.Item key="all">Select All</Menu.Item>
    </Menu>
  );

  // Table columns
  const columns = [
    {
      title: (
        <Checkbox
          checked={selectAll}
          indeterminate={selectedPlans.length > 0 && selectedPlans.length < flattenedPlans.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'select',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedPlans.includes(record.id)}
          onChange={(e) => handlePlanSelect(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (type) => getPlanTypeBadge(type),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title, record) => (
        <span style={{ paddingLeft: record.level * 20 }}>{title}</span>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (unit, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Select
            value={editingData.unit || unit}
            onChange={(value) => handleFieldChange('unit', value)}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {(units || []).filter(u => u && u.id && u.name).map(u => (
              <Option key={u.id} value={u.name}>{u.name}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'unit')}>{unit}</div>
        );
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      width: 200,
      render: (assignedTo, record) => {
        const editing = isEditing(record);
        if (!editing) {
          return <div style={getCellStyle(record, 'assignedTo')}>{assignedTo}</div>;
        }
        
        // Show positions for the selected unit
        return (
          <Select
            value={editingData.assignedTo || assignedTo}
            onChange={(value) => handleFieldChange('assignedTo', value)}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            placeholder={positions.length === 0 ? 'No positions in this unit' : 'Select position'}
          >
            {positions.map(pos => (
              <Option key={pos.id} value={`${pos.user?.firstName}, ${pos.designation?.name}`}>
                {pos.user?.firstName} {pos.user?.lastName} - {pos.designation?.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      width: 100,
    },
    {
      title: 'Target Date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      width: 120,
      render: (targetDate) => moment(targetDate).format('DD/MM/YYYY'),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (description) => description || '-',
    },
    {
      title: 'Plan Group',
      dataIndex: 'planGroup',
      key: 'planGroup',
      width: 120,
      render: (planGroup, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Select
            value={editingData.planGroup || planGroup}
            onChange={(value) => handleFieldChange('planGroup', value)}
            style={{ width: '100%' }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {(planGroups || []).filter(pg => pg && pg.id && pg.name).map(pg => (
              <Option key={pg.id} value={pg.name}>{pg.name}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'planGroup')}>{planGroup || '-'}</div>
        );
      },
    },
    {
      title: 'Target Scale',
      dataIndex: 'scaleId',
      key: 'scaleId',
      width: 120,
      render: (scaleId, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Select
            value={editingData.scaleId || scaleId}
            onChange={(value) => handleFieldChange('scaleId', value)}
            style={{ width: '100%' }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {(scales || []).filter(s => s && s.id && s.name).map(s => (
              <Option key={s.id} value={s.name}>{s.name}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'scaleId')}>{scaleId || '-'}</div>
        );
      },
    },
    {
      title: 'Project.No',
      key: 'projectNo',
      width: 120,
      render: (_, record) => {
        const value = record.customAttributes?.['Project.No'];
        const editing = isEditing(record);
        const dropdownValues = dropdownAttributeValues['Project.No'] || [];
        return editing ? (
          <Select
            value={editingData.customAttributes?.['Project.No'] || value}
            onChange={(value) => handleFieldChange('customAttributes.Project.No', value)}
            style={{ width: '100%' }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {dropdownValues.filter(dv => dv && dv.id && dv.value).map(dv => (
              <Option key={dv.id} value={dv.value}>{dv.value}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'Project.No')}>{value || '-'}</div>
        );
      },
    },
    {
      title: 'Responsible',
      key: 'responsible',
      width: 120,
      render: (_, record) => {
        const value = record.customAttributes?.['Responsible:'];
        const editing = isEditing(record);
        const dropdownValues = dropdownAttributeValues['Responsible:'] || [];
        return editing ? (
          <Select
            value={editingData.customAttributes?.['Responsible:'] || value}
            onChange={(value) => handleFieldChange('customAttributes.Responsible:', value)}
            style={{ width: '100%' }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {dropdownValues.filter(dv => dv && dv.id && dv.value).map(dv => (
              <Option key={dv.id} value={dv.value}>{dv.value}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'Responsible:')}>{value || '-'}</div>
        );
      },
    },
    {
      title: 'SO.No',
      key: 'soNo',
      width: 120,
      render: (_, record) => {
        const value = record.customAttributes?.['SO.No'];
        const editing = isEditing(record);
        const dropdownValues = dropdownAttributeValues['SO.No'] || [];
        return editing ? (
          <Select
            value={editingData.customAttributes?.['SO.No'] || value}
            onChange={(value) => handleFieldChange('customAttributes.SO.No', value)}
            style={{ width: '100%' }}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {dropdownValues.filter(dv => dv && dv.id && dv.value).map(dv => (
              <Option key={dv.id} value={dv.value}>{dv.value}</Option>
            ))}
          </Select>
        ) : (
          <div style={getCellStyle(record, 'SO.No')}>{value || '-'}</div>
        );
      },
    },
    {
      title: 'Is Active',
      key: 'isActive',
      width: 100,
      render: (_, record) => {
        const value = record.customAttributes?.['Is Active'];
        return <div style={getCellStyle(record, 'Is Active')}>{value || '-'}</div>;
      },
    },
    {
      title: 'Hierarchy Order',
      key: 'hierarchyOrder',
      width: 120,
      render: (_, record) => {
        return <div>{record.hierarchyOrder !== null && record.hierarchyOrder !== undefined ? record.hierarchyOrder : '-'}</div>;
      },
    },
    {
      title: 'Project Weight',
      key: 'projectWeight',
      width: 120,
      render: (_, record) => {
        const value = record.customAttributes?.['Project Weight'];
        return <div>{value || '-'}</div>;
      },
    },
    {
      title: 'Remark',
      key: 'remark',
      width: 150,
      render: (_, record) => {
        const value = record.customAttributes?.['Remark'];
        const editing = isEditing(record);
        return editing ? (
          <Input
            value={editingData.customAttributes?.['Remark'] !== undefined ? editingData.customAttributes?.['Remark'] : value}
            onChange={(e) => handleFieldChange('customAttributes.Remark', e.target.value)}
          />
        ) : (
          <div>{value || '-'}</div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
          </Space>
        ) : (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={editingKey !== '' || loadingDropdowns}
            loading={loadingDropdowns}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Validation Status"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ display: 'inline-block', width: 20, height: 20, backgroundColor: '#52c41a', border: '2px solid #52c41a', marginRight: 8, verticalAlign: 'middle' }}></span>
                <span>Green border: Value matched in database</span>
              </div>
              <div>
                <span style={{ display: 'inline-block', width: 20, height: 20, backgroundColor: '#faad14', border: '2px solid #faad14', marginRight: 8, verticalAlign: 'middle' }}></span>
                <span>Yellow border: Value not found (will still be created, but may need review)</span>
              </div>
            </div>
          }
          type="info"
          showIcon
          closable
        />
      </div>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ marginRight: 8 }}>
            Selected: {selectedPlans.length} / {flattenedPlans.length}
          </span>
          <Dropdown overlay={batchMenu}>
            <Button>
              Batch Select <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={flattenedPlans}
        rowKey="id"
        pagination={false}
        scroll={{ x: 2700, y: 500 }}
        size="small"
      />
    </div>
  );
}

export default PlanPreviewTable;
